import {
    IGeneralReportsConfig,
    IPlayerReportDataStreams,
    PLAYER_REPORT_DATA_STREAMS,
    REPORT_TYPES,
} from "@components/Report/constants";

/**
 * A metric slot as persisted in the report config. The metric picker stores
 * `{ metric, agg }` tuples (with `metric` undefined for slots the user has not
 * filled in yet), while the plain multi-select pickers store bare metric ids.
 */
export type MetricSlot = { metric?: string; agg?: string } | string | undefined | null;

export interface IMetricGroup {
    /** config key prefix — the metrics live at `${key}-metrics` */
    key: string;
    label: string;
    count: number;
}

export interface IIncompleteMetricGroup extends IMetricGroup {
    selected: number;
}

// The weekly report asks for 4 metrics per data stream; wellness needs none.
const WEEKLY_METRIC_GROUPS: IMetricGroup[] = [
    { key: 'gps', label: 'GPS', count: 4 },
    { key: 'testing', label: 'Testing', count: 4 },
    { key: 'forceplate', label: 'Forceplate', count: 4 },
];

export const isMetricSlotFilled = (slot: MetricSlot): boolean =>
    typeof slot === 'string' ? slot.trim() !== '' : Boolean(slot?.metric);

export const countFilledMetrics = (slots?: MetricSlot[]): number =>
    (slots || []).filter(isMetricSlotFilled).length;

export const getFilledMetricIds = (slots?: MetricSlot[]): string[] =>
    (slots || [])
        .filter(isMetricSlotFilled)
        .map((slot) => (typeof slot === 'string' ? slot : (slot as { metric: string }).metric));

/**
 * How many metrics a metric-picker prefix expects. A prefix is either a report
 * type key (single metric selector) or a data stream key (player/weekly reports
 * with one selector per stream).
 */
export const getExpectedMetricCount = (prefix?: string): number => {
    if (!prefix) return 0;
    const reportCount = REPORT_TYPES.find((report) => report.key === prefix)?.metric_selector_info?.count;
    if (reportCount) return reportCount;
    return PLAYER_REPORT_DATA_STREAMS.find((stream) => stream.key === prefix)?.count || 0;
};

/**
 * Fixed-size metric selections the report requires, given the data streams the
 * user has picked. Reports whose selector accepts a variable number of metrics
 * (player_date_range_report) have no fixed-size group to complete.
 */
export const getRequiredMetricGroups = (reportKey?: string, config?: IGeneralReportsConfig): IMetricGroup[] => {
    const reportObj = REPORT_TYPES.find((report) => report.key === reportKey);
    if (!reportObj) return [];

    if (reportKey === 'player_report') {
        return ((config?.player_report_data_streams || [])
            .map((streamKey) => PLAYER_REPORT_DATA_STREAMS.find((stream) => stream.key === streamKey))
            .filter(Boolean) as IPlayerReportDataStreams[])
            .map(({ key, label, count }) => ({ key, label, count }));
    }

    if (reportKey === 'weekly') {
        return WEEKLY_METRIC_GROUPS.filter((group) => (config?.weekly_streams || []).includes(group.key));
    }

    if (reportKey === 'player_date_range_report' || !reportObj.requirements?.includes('metric_selector')) {
        return [];
    }

    const count = reportObj.metric_selector_info?.count || 0;
    return count ? [{ key: reportObj.key, label: reportObj.label, count }] : [];
};

export const getIncompleteMetricGroups = (
    reportKey?: string,
    config?: IGeneralReportsConfig,
): IIncompleteMetricGroup[] =>
    getRequiredMetricGroups(reportKey, config)
        .map((group) => ({ ...group, selected: countFilledMetrics((config as any)?.[`${group.key}-metrics`]) }))
        .filter((group) => group.selected !== group.count);

export const getDuplicateMetricGroups = (reportKey?: string, config?: IGeneralReportsConfig): IMetricGroup[] => {
    if (!REPORT_TYPES.find((report) => report.key === reportKey)?.metric_selector_info?.unique) return [];

    return getRequiredMetricGroups(reportKey, config).filter((group) => {
        const metricIds = getFilledMetricIds((config as any)?.[`${group.key}-metrics`]);
        return metricIds.length !== new Set(metricIds).size;
    });
};

export const formatIncompleteMetricsMessage = (groups: IIncompleteMetricGroup[]): string => {
    if (groups.length === 1) {
        const [{ label, selected, count }] = groups;
        return `Metric selection for ${label} is incomplete (${selected}/${count} selected). Please select all ${count} metrics before generating the report.`;
    }

    const summary = groups.map(({ label, selected, count }) => `${label} (${selected}/${count})`).join(', ');
    return `Metric selection is incomplete for ${summary}. Please complete every metric selection before generating the report.`;
};

export const formatDuplicateMetricsMessage = (groups: IMetricGroup[]): string =>
    `Duplicate metrics selected for ${groups.map(({ label }) => label).join(', ')}. Please select unique metrics before generating the report.`;
